import { motion } from "framer-motion";
import { Popcorn, Gamepad2, Video, GraduationCap } from "lucide-react";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const MOMENTS = [
  { icon: Popcorn, t: "Movie Nights", d: "Stream 4K on every TV at once — zero buffering, zero spinning wheels.", ring: "border-pink-400/30", tile: "bg-pink-500/10 text-pink-300", glow: "bg-pink-500/20" },
  { icon: Gamepad2, t: "Lag-Free Gaming", d: "Low latency and consistent speeds keep the whole squad in the game.", ring: "border-violet-400/30", tile: "bg-violet-500/10 text-violet-300", glow: "bg-violet-500/20" },
  { icon: Video, t: "Video Calls", d: "Work, school and grandkids on FaceTime — clear and uninterrupted.", ring: "border-sky-400/30", tile: "bg-sky-500/10 text-sky-300", glow: "bg-sky-500/20" },
  { icon: GraduationCap, t: "Homework & WFH", d: "Enough bandwidth for everyone's homework, cloud files and big uploads.", ring: "border-cyan-400/30", tile: "bg-cyan-500/10 text-cyan-300", glow: "bg-cyan-500/20" },
];

export default function FamilyMoments() {
  return (
    <section className="relative py-24 sm:py-32 bg-ink-950 overflow-hidden" data-testid="family-section">
      <div className="absolute top-10 right-0 w-[420px] h-[420px] rounded-full bg-pink-500/10 blur-[130px]" />
      <div className="absolute bottom-10 left-0 w-[420px] h-[420px] rounded-full bg-violet-500/10 blur-[130px]" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center relative">
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-tr from-violet-500/20 via-pink-500/20 to-sky-500/20 blur-2xl rounded-[2rem]" />
            <motion.div
              initial={{ clipPath: "inset(0 0 100% 0)" }}
              whileInView={{ clipPath: "inset(0 0 0% 0)" }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-[1.75rem] overflow-hidden border border-white/10"
            >
              <img src={IMAGES.familyMovie} alt="Family enjoying a buffer-free movie night at home" className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 to-transparent" />
              <div className="absolute bottom-5 left-5 font-mono-tech text-xs uppercase tracking-widest text-pink-300">// Movie night, sorted</div>
            </motion.div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="overline mb-4 text-pink-300">For the whole family</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-[0.92]">
            Finally, buffer-free <span className="text-aurora">movie nights.</span>
          </h2>
          <p className="mt-6 text-lg text-zinc-400 leading-relaxed max-w-xl">
            One connection the whole house can count on. Whether it's a Friday film, a boss raid, a video call with grandma, or homework crunch — CYAN keeps everyone happy at the same time.
          </p>

          <Stagger className="mt-10 grid sm:grid-cols-2 gap-4">
            {MOMENTS.map((m) => (
              <motion.div key={m.t} variants={staggerItem} className={`group relative bg-ink-900 border ${m.ring} rounded-2xl p-6 overflow-hidden transition-transform hover:-translate-y-1`}>
                <div className={`absolute -top-14 -right-14 w-32 h-32 rounded-full ${m.glow} blur-2xl opacity-0 group-hover:opacity-100 transition-opacity`} />
                <div className={`relative w-11 h-11 grid place-items-center rounded-xl ${m.tile} mb-4`}>
                  <m.icon size={20} />
                </div>
                <h3 className="relative font-display font-bold text-lg text-white mb-1">{m.t}</h3>
                <p className="relative text-zinc-400 text-sm leading-relaxed">{m.d}</p>
              </motion.div>
            ))}
          </Stagger>
        </Reveal>
      </div>
    </section>
  );
}
